import { useState } from 'react';
import { useT, type MessageKey } from '../lib/i18n';
import type { TreeEntry } from '../lib/taxonomyGraph';

/**
 * The taxonomy as a tree (docs/en/43 sec.8): each root's templates under the ones they extend,
 * the value types, and each kind of directive. The same definition as the graph, read the way
 * a person reads a list.
 */
export function TaxonomyTree({ groups, selected, onSelect, problems }: {
  groups: { group: string; entries: TreeEntry[] }[];
  selected: string | null;
  onSelect: (id: string) => void;
  problems: Set<string>;
}) {
  const { t } = useT();
  return (
    <div className="taxonomy-tree" data-testid="taxonomy-tree">
      {groups.map((g) => (
        <details key={g.group} open className="tx-group">
          <summary>{t(groupLabel(g.group))} <span className="hint">{count(g.entries)}</span></summary>
          <ul>{g.entries.map((e) => <Branch key={e.id} entry={e} selected={selected} onSelect={onSelect} problems={problems} />)}</ul>
        </details>
      ))}
    </div>
  );
}

function groupLabel(group: string): MessageKey {
  return (['object', 'event', 'document', 'value', 'directive'].includes(group)
    ? `taxonomy.lane.${group}` : `taxonomy.kinds.${group}`) as MessageKey;
}

function count(entries: TreeEntry[]): number {
  return entries.reduce((n, e) => n + 1 + count(e.children), 0);
}

function Branch({ entry, selected, onSelect, problems }: {
  entry: TreeEntry; selected: string | null; onSelect: (id: string) => void; problems: Set<string>;
}) {
  const [open, setOpen] = useState(true);
  const classes = ['tx-entry'];
  if (entry.id === selected) classes.push('on');
  if (entry.isAbstract) classes.push('abstract');
  if (problems.has(entry.id)) classes.push('bad');
  return (
    <li>
      <div className={classes.join(' ')}>
        {entry.children.length > 0
          ? <button className="tx-toggle" onClick={() => setOpen(!open)} aria-expanded={open}>{open ? '▾' : '▸'}</button>
          : <span className="tx-toggle" />}
        <button className="tx-name" onClick={() => onSelect(entry.id)} data-testid={`tx-tree-${entry.id}`}>
          <span>{entry.label}</span> <code>{entry.key}</code>
        </button>
      </div>
      {open && entry.children.length > 0 && (
        <ul>{entry.children.map((c) => <Branch key={c.id} entry={c} selected={selected} onSelect={onSelect} problems={problems} />)}</ul>
      )}
    </li>
  );
}
